import React, { useContext, useRef, useState } from "react";

import "react-phone-input-2/lib/style.css";
import * as Yup from "yup";

import AuthContext from "shared/providers/context/AuthContext";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";
import openNotificationWithIcon from "shared/utils/openNotificationWithIcon";

import Button from "@CheckoutStepsOffers/components/atoms/Button";
import DocumentInput from "@CheckoutStepsOffers/components/atoms/DocumentInput";
import Input from "@CheckoutStepsOffers/components/atoms/Input";
import SectionHeader from "@CheckoutStepsOffers/components/templates/SectionHeader";
import { getInformationsSchemaValidation } from "@CheckoutStepsOffers/schema/getInformationsSchemaValidation";
import { WrapperInputs } from "@CheckoutStepsOffers/styles/WrapperInputs";
import { getRegisterUserPayload } from "@CheckoutStepsOffers/utils/getRegisterUserPayload";
import PhoneInputCountry from "@payment/components/PhoneInputCountry";
import SelectCountry from "@payment/components/SelectCountry";
import { useCheckoutMetrics } from "@payment/hooks/useCheckoutMetrics";
import useCheckoutStepsOffersContext, {
  IInformations,
} from "@payment/pages/CheckoutStepsOffers/provider";
import { useCheckoutContext } from "@payment/providers/CheckoutProvider/useCheckoutContext";
import { savePaymentDetails } from "@payment/utils/savePaymentDetails";
import { FormHandles, SubmitHandler } from "@unform/core";

import { LoadingIcon } from "../Payment/styles";
import { Container, Form } from "./styles";

const Informations = () => {
  const { state } = useGlobalContext();
  const { loggedUser, registerWithoutPassword } = useContext(AuthContext);
  const {
    userSettingsState,
    setNewUserSettings,
    setCountry,
    country,
    setCountryDDI,
    countryId,
    setCountryId,
  } = useCheckoutContext();
  const {
    informations,
    setInformations,
    setSteps,
    offer,
    initialData,
    userCanTrial,
    setMethodPaymentSelected,
    isPhoneNumberValid,
    setIsPhoneNumberValid,
  } = useCheckoutStepsOffersContext();

  const { startingPurchaseProcess } = useCheckoutMetrics();

  const formRef = useRef<FormHandles>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [inputError, setInputError] = useState<string>("");

  const descriptionButton =
    !!offer?.offer?.details?.trial_enabled && userCanTrial
      ? "Quero aproveitar o período gratuito"
      : "Continuar";

  const registerNewUser = async (requestBody) => {
    const response = await registerWithoutPassword(requestBody);

    return response;
  };

  const { create_account_options } = state?.tenancy;

  const handleSubmit: SubmitHandler<IInformations> = async (data) => {
    const schema = getInformationsSchemaValidation(
      !!loggedUser,
      create_account_options,
      country,
    );

    try {
      setLoader(true);

      await schema.validate(data, {
        abortEarly: false,
      });

      if (loggedUser) {
        startingPurchaseProcess(offer.uuid);
      } else {
        const requestBody = {
          ...getRegisterUserPayload(data),
          country_id: countryId,
        };
        const response = await registerNewUser(requestBody);

        if (response === "success") {
          startingPurchaseProcess(offer.uuid);
          savePaymentDetails(requestBody, setNewUserSettings);
        }
      }

      setLoader(false);
      setInformations(data);
      setSteps((prevState) => prevState + 1);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};

        error.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });

        formRef.current.setErrors(validationErrors);

        openNotificationWithIcon(
          "error",
          "Campos incorretos",
          "Preencha os campos corretamente antes de prosseguir",
        );
      }

      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const emailFieldRegex = /[^a-zA-Zs0-9@._-]/g;

  const phoneNumber = !!loggedUser?.phones?.length
    ? loggedUser?.phones[0]
    : null;

  const getInitialData = () => {
    const payment = userSettingsState?.userSettings?.payment;

    const display_name =
      payment?.display_name ||
      payment?.fullname ||
      loggedUser?.display_name ||
      loggedUser?.full_name;
    const document = payment?.document || loggedUser?.document;
    const phone = phoneNumber?.full_number || payment?.phone;

    return {
      email: initialData?.email,
      name: display_name || initialData?.name,
      document: document || initialData?.document,
      phone_number: phone?.slice(1) || initialData?.phone,
    };
  };

  const initialDataUserSettings = getInitialData();

  const handleChangeCountry = (selectedOption) => {
    setCountry(selectedOption?.name);
    setCountryDDI(selectedOption?.ddi);
    setCountryId(selectedOption?.code);
    setMethodPaymentSelected("credit_card");
  };

  return (
    <Container>
      <Form
        ref={formRef}
        onSubmit={handleSubmit}
        initialData={informations || initialDataUserSettings}
      >
        <div className="report-with-select-country">
          <SectionHeader title="Informações básicas" />

          <SelectCountry handleChange={handleChangeCountry} />
        </div>

        <WrapperInputs>
          <Input
            required
            name="name"
            label="Nome Completo"
            placeholder="Digite o seu nome completo"
          />
        </WrapperInputs>

        {!loggedUser && (
          <WrapperInputs>
            <Input
              required
              type="email"
              name="email"
              label="E-mail"
              replace={emailFieldRegex}
              placeholder="Digite o seu e-mail"
            />
          </WrapperInputs>
        )}

        <WrapperInputs>
          {country === "Brasil" && (
            <DocumentInput
              required
              name="document"
              label="CPF ou CNPJ"
              inputError={inputError}
              setInputError={setInputError}
              placeholder="000.000.000-00"
            />
          )}
          <PhoneInputCountry setIsPhoneNumberValid={setIsPhoneNumberValid} />
        </WrapperInputs>

        {!loggedUser &&
          create_account_options?.request_custom_fields_to_create_account &&
          create_account_options?.custom_fields_active?.map((field) => (
            <WrapperInputs key={field?.name}>
              <Input
                required={field?.required}
                name={field?.name}
                label={field?.label}
                placeholder={field?.placeholder}
              />
            </WrapperInputs>
          ))}

        <Button type="submit" disabled={!!inputError || !isPhoneNumberValid}>
          {loader && <LoadingIcon />}
          {descriptionButton}
        </Button>
      </Form>
    </Container>
  );
};

export default Informations;
